import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Splide, { EventMap, Options } from '@splidejs/splide'

interface PaginationContainerProps {
  indexOffset: number
  portrait?: boolean
}

const PaginationContainer = styled.div<PaginationContainerProps>`
  position: absolute;
  bottom: ${({ portrait }) => (portrait ? '13px' : '6px')};
  left: 0;
  right: 0;
  margin: auto;
  width: 75px;
  height: 30px;
  overflow: hidden;

  .splide__pagination {
    position: absolute;
    padding: 0;
    width: 120px;
    left: 50%;
    transform: ${({ indexOffset }) =>
      `translateX(-${indexOffset * 14 + 7}px);`};
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    transition: transform 200ms linear;
  }

  .splide__pagination__page {
    opacity: 0.6;

    &.is-active {
      opacity: 1;
    }
  }
`

type UseSplideOptions = {
  nodeRef: React.MutableRefObject<HTMLElement>
  onMove?: EventMap['move']
  portrait?: boolean
  showArrows?: boolean
  type?: string
}

export const useInstantiateSplide = ({
  nodeRef,
  onMove,
  portrait,
  showArrows,
  type = 'slide',
}: UseSplideOptions) => {
  let splide: Splide

  useEffect(() => {
    if (nodeRef.current != null) {
      const options: Options = {
        type,
        pagination: true,
        arrows: showArrows,
        heightRatio: portrait ? 1.25 : 0.75,
      }

      splide = new Splide(nodeRef.current, options)
      splide.mount()

      splide.on('move', onMove)
    }

    return () => {
      if (splide) {
        splide.destroy()
      }
    }
  }, [nodeRef, showArrows])
}

type KeyedSlide = {
  key: string
  Component: React.ComponentType<{ isActive: boolean }>
}

type SplideSliderProps = {
  title: string
  portrait?: boolean
  keyedSlides: KeyedSlide[]
  arrows?: React.ReactNode
  showArrows?: boolean
  type?: string
}

const SplideSlider = ({
  title,
  portrait,
  keyedSlides,
  arrows,
  showArrows,
  type,
}: SplideSliderProps) => {
  const [slideIndex, setSlideIndex] = useState(0)
  const splideRef = useRef<HTMLDivElement>()

  const onMove: EventMap['move'] = (index) => {
    setSlideIndex(index)
  }

  useInstantiateSplide({
    type,
    nodeRef: splideRef,
    showArrows,
    onMove,
    portrait,
  })

  const ariaLabel = `Images for ${title}`

  return (
    <div className="splide" aria-label={ariaLabel} ref={splideRef}>
      <div className="splide__track">
        <ul className="splide__list">
          {keyedSlides.map(({ key, Component }, index) => {
            return (
              <li className="splide__slide" key={key}>
                <Component isActive={slideIndex === index} />
              </li>
            )
          })}
        </ul>
      </div>

      <PaginationContainer indexOffset={slideIndex} portrait={portrait}>
        {/* eslint-disable-next-line react/self-closing-comp */}
        <ul className="splide__pagination"></ul>
      </PaginationContainer>

      {arrows && <div className="splide__arrows">{arrows}</div>}
    </div>
  )
}

export default SplideSlider
